import { bus } from './bus.js';

/** An error response from the API (or a failure to reach it). `status` 0 means the network failed. */
export class ApiError extends Error {
  constructor(status, code, message, details = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** Field-level messages from a 422, as { field: message }. */
  get fieldErrors() {
    const out = {};
    if (Array.isArray(this.details)) {
      for (const d of this.details) if (d && d.field && !(d.field in out)) out[d.field] = d.message;
    }
    return out;
  }
}

const FRIENDLY = {
  0: 'We could not reach the server. Check your connection and try again.',
  413: 'That is too large to send.',
  429: 'Too many attempts. Please wait a moment and try again.',
  500: 'Something went wrong on our side. Please try again.',
  502: 'The service is temporarily unavailable. Please try again shortly.',
  503: 'The service is temporarily unavailable. Please try again shortly.',
  504: 'The server took too long to answer. Please try again.',
};

/** A message that is safe to show a person. */
export function messageFor(error) {
  if (error instanceof ApiError) {
    if (error.status >= 500 || error.status === 0 || error.status === 429) return FRIENDLY[error.status] || error.message;
    return error.message || FRIENDLY[error.status] || 'Something went wrong.';
  }
  return 'Something went wrong. Please try again.';
}

let reporter = null;

/** Optional hook for an error-tracking service (set in main.js when configured). */
export function setErrorReporter(fn) {
  reporter = fn;
}

/**
 * Central place for unexpected failures. Shows a toast unless silent, logs to the console in development,
 * and forwards to the error tracker if one is configured. 401s are handled by the API client, not here.
 */
export function reportError(error, { silent = false } = {}) {
  if (error instanceof ApiError && error.status === 401) return;
  if (!(error instanceof ApiError)) console.error(error);
  if (reporter && !(error instanceof ApiError && error.status < 500 && error.status !== 0)) {
    try {
      reporter(error);
    } catch {
      /* the reporter must never break the app */
    }
  }
  if (!silent) bus.emit('toast', { message: messageFor(error), icon: 'alert' });
}
